import apiClient from "repositories/apiClient";
import { sortServicesByIndex } from "lib/services";
import { createReservationParam, getAvailabilityParam } from "./reservationRepository.param";

// Strip duplicate optionIds before sending to the backend.
// The frontend builds optionIds from many mutation sites in Start.tsx and
// StartContent/index.tsx, none of which guard against duplicates. The backend
// rejects duplicate IDs, so we deduplicate at this single transport boundary.
const dedupeOptionIds = (optionIds: string[]): string[] => Array.from(new Set(optionIds));

export const getSettings = async (customerId: string) => {
  return await apiClient({
    method: "get",
    url: `public/${customerId}/widget/settings`,
  });
};

export const getServices = async (customerId: string) => {
  const response = await apiClient({
    method: "get",
    url: `public/${customerId}/services`,
  });

  if (Array.isArray(response.data.result)) {
    response.data.result = sortServicesByIndex(response.data.result);
  }

  return response;
};

export const getReferrals = async (customerId: string) => {
  return await apiClient({
    method: "get",
    url: `public/${customerId}/referrals`,
  });
};

export const getBranches = async (customerId: string) => {
  return await apiClient({
    method: "get",
    url: `public/${customerId}/branches`,
  });
};

export const getWhois = async (customerId: string, userId: string, branchId: string) => {
  return await apiClient({
    method: "get",
    url: `public/${customerId}/users/${userId}/payments/whois?branchId=${branchId}`,
  });
};

export const getAvailability = async (input: getAvailabilityParam) => {
  const { customerId, dateFrom, dateTo, branchIds, serviceIds, optionIds, therapistIds, ...rest } = input;

  const params = {
    dateFrom,
    dateTo,
  };

  if (branchIds) {
    params["branchIds"] = branchIds.join(",");
  }
  if (serviceIds) {
    params["serviceIds"] = serviceIds.join(",");
  }
  if (optionIds) {
    params["optionIds"] = dedupeOptionIds(optionIds).join(",");
  }
  if (therapistIds) {
    params["therapistIds"] = therapistIds.join(",");
  }

  return await apiClient({
    method: "get",
    url: `public/${customerId}/availability`,
    params: {
      ...params,
      ...rest,
    },
  });
};

export const createReservation = async ({ data }: { data: createReservationParam }) => {
  return await apiClient({
    method: "post",
    url: `public/reservation`,
    data: { ...data, optionIds: dedupeOptionIds(data.optionIds) },
  });
};

export const sendTracking = async ({ data }: { data: { customerId: string; action: string } }) => {
  const { customerId, action } = data;
  return await apiClient({
    method: "post",
    url: `public/${customerId}/widget/track`,
    data: {
      action,
    },
  });
};
